import React, { useState, useEffect, useMemo } from 'react';
import { summaryApi, unitsApi, settlementApi, loadsApi, deductionsApi, settlementEmailsApi, featureApi, preferencesApi } from '../lib/api';
import { ArrangeUnitsDialog } from '../components/summary/ArrangeUnitsDialog';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  FileText, 
  Download,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  Pencil,
  Trash2,
  Plus,
  Truck,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Share2,
  Mail,
  Send,
  MessageCircle,
  Package,
  MapPin,
  Fuel,
  X,
  CheckCircle,
  StickyNote,
  AlertCircle,
  BarChart3,
  ArrowUpDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatCurrency, getWeekStart, getWeekRange, formatDate, addDaysYMD } from '../lib/utils';
import UnitSelectItems from '../components/UnitSelectItems';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import DateRangePicker from '../components/DateRangePicker';
import { DashCard, DashTooltip, DASH_COLORS, compactMoney } from '../components/dashboard/DashCard';
import { StatTile } from '../components/dashboard/KpiStrip';
import { UnitBarsList } from '../components/dashboard/UnitBarsList';
import { RevenueSplitCard } from '../components/dashboard/RevenueSplitCard';
import { DriverPaystubsTab } from '../components/paystubs/DriverPaystubsTab';
import { EmailPromptDialog } from '../components/paystubs/EmailPromptDialog';

export default function Summary() {
  const { isDriver, user, canEdit } = useAuth();
  const [summaries, setSummaries] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [unitOrder, setUnitOrder] = useState([]);
  const [arrangeOpen, setArrangeOpen] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  useEffect(() => {
    preferencesApi.getSummaryUnitOrder().then((r) => setUnitOrder(r.data?.unit_ids || [])).catch(() => {});
  }, []);
  const [weekStart, setWeekStart] = useState(getWeekStart());
  // Date filter mode: 'week' (default) or 'range' (custom range)
  const [dateMode, setDateMode] = useState('week');
  const [summaryTab, setSummaryTab] = useState('units');
  const [settlementEmailUnit, setSettlementEmailUnit] = useState(null);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [exporting, setExporting] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [bulkEmailing, setBulkEmailing] = useState(false);
  const [bulkEmailEnabled, setBulkEmailEnabled] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState({});
  
  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfUnitNumber, setPdfUnitNumber] = useState('');
  const [itemizedDeductions, setItemizedDeductions] = useState(false);

  // Settlement status and payroll notes state
  const [settlementStatus, setSettlementStatus] = useState({});
  const [payrollNotes, setPayrollNotes] = useState({});
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');

  // Inline Load Editing state
  const [editLoadDialogOpen, setEditLoadDialogOpen] = useState(false);
  const [editingLoad, setEditingLoad] = useState(null);
  const [loadFormData, setLoadFormData] = useState({
    load_reference: '',
    broker_name: '',
    pickup_location: '',
    delivery_location: '',
    start_date: '',
    end_date: '',
    payout_amount: '',
    fee_type: 'percentage',
    fee_value: '',
    unit_id: '',
  });

  // Inline Deduction Editing state
  const [editDeductionDialogOpen, setEditDeductionDialogOpen] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [deductionFormData, setDeductionFormData] = useState({
    name: '',
    amount: '',
    deduction_type: 'standard',
    notes: '',
    unit_id: '',
  });

  // Previous Balance Breakdown Dialog state
  const [prevBalanceDialogOpen, setPrevBalanceDialogOpen] = useState(false);
  const [prevBalanceData, setPrevBalanceData] = useState(null);
  const [loadingPrevBalance, setLoadingPrevBalance] = useState(false);

  // Check if native share is supported
  const canNativeShare = navigator.share && navigator.canShare;

  const toggleUnitExpand = (unitId) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  };

  useEffect(() => {
    loadUnits();
    // Load feature toggles to know whether to show the Bulk Email button.
    featureApi.getToggles()
      .then(r => setBulkEmailEnabled(!!r.data?.bulk_email_settlements))
      .catch(() => setBulkEmailEnabled(false));
    // Only on mount — loadUnits identity would otherwise re-fire this on every
    // render even though we truly want just an initial fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, dateMode, rangeStart, rangeEnd]);

  const loadUnits = async () => {
    try {
      const res = await unitsApi.getAll();
      setUnits(res.data);
    } catch (error) {
      toast.error('Failed to load units');
    }
  };

  // Open edit dialog for a load
  const handleEditLoad = (load) => {
    setEditingLoad(load);
    setLoadFormData({
      load_reference: load.load_reference || '',
      broker_name: load.broker_name || '',
      pickup_location: load.pickup_location || '',
      delivery_location: load.delivery_location || '',
      start_date: load.start_date || '',
      end_date: load.end_date || '',
      payout_amount: load.payout_amount?.toString() || '',
      fee_type: load.fee_type || 'percentage',
      fee_value: load.fee_value?.toString() || '',
      unit_id: load.unit_id || '',
    });
    setEditLoadDialogOpen(true);
  };

  // Delete load
  const handleDeleteLoad = async (loadId) => {
    if (!window.confirm('Are you sure you want to delete this load?')) return;
    try {
      await loadsApi.delete(loadId);
      toast.success('Load deleted successfully');
      loadSummaries();
    } catch (error) {
      toast.error('Failed to delete load');
    }
  };

  // Open edit dialog for a deduction
  const handleEditDeduction = (deduction) => {
    setEditingDeduction(deduction);
    setDeductionFormData({
      name: deduction.name || '',
      amount: deduction.amount?.toString() || '',
      deduction_type: deduction.deduction_type || 'standard',
      notes: deduction.notes || '',
      unit_id: deduction.unit_id || '',
    });
    setEditDeductionDialogOpen(true);
  };

  // Delete deduction
  const handleDeleteDeduction = async (deductionId) => {
    if (!window.confirm('Are you sure you want to delete this deduction?')) return;
    try {
      await deductionsApi.delete(deductionId);
      toast.success('Deduction deleted successfully');
      loadSummaries();
    } catch (error) {
      toast.error('Failed to delete deduction');
    }
  };

  // Open dialog for ADDING a new deduction directly from the summary view
  const handleAddDeduction = (unitId, summaryWeekStart) => {
    setEditingDeduction({ id: null, unit_id: unitId, week_start: summaryWeekStart });
    setDeductionFormData({
      name: '',
      amount: '',
      deduction_type: 'standard',
      notes: '',
      unit_id: unitId,
    });
    setEditDeductionDialogOpen(true);
  };

  // Save deduction — handles both Add (no id) and Edit (with id)
  const handleSaveDeduction = async () => {
    try {
      const payload = {
        name: deductionFormData.name,
        amount: parseFloat(deductionFormData.amount) || 0,
        deduction_type: deductionFormData.deduction_type,
        notes: deductionFormData.notes,
        unit_id: editingDeduction.unit_id,
        week_start: editingDeduction.week_start,
      };
      if (!payload.name?.trim()) { toast.error('Name is required'); return; }
      if (payload.amount <= 0) { toast.error('Amount must be greater than 0'); return; }

      if (editingDeduction.id) {
        await deductionsApi.update(editingDeduction.id, payload);
        toast.success('Deduction updated');
      } else {
        await deductionsApi.create(payload);
        toast.success('Deduction added');
      }
      setEditDeductionDialogOpen(false);
      setEditingDeduction(null);
      loadSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save deduction');
    }
  };

  // Fetch Previous Balance breakdown
  const handleViewPreviousBalance = async (unitId, weekStart) => {
    setLoadingPrevBalance(true);
    setPrevBalanceDialogOpen(true);
    try {
      const res = await summaryApi.getPreviousBalanceBreakdown(unitId, weekStart);
      setPrevBalanceData(res.data);
    } catch (error) {
      toast.error('Failed to load previous balance details');
      setPrevBalanceDialogOpen(false);
    } finally {
      setLoadingPrevBalance(false);
    }
  };

  // Save edited load
  const handleSaveLoad = async () => {
    try {
      const updateData = {
        load_reference: loadFormData.load_reference,
        broker_name: loadFormData.broker_name,
        pickup_location: loadFormData.pickup_location,
        delivery_location: loadFormData.delivery_location,
        start_date: loadFormData.start_date,
        end_date: loadFormData.end_date,
        payout_amount: parseFloat(loadFormData.payout_amount) || 0,
        fee_type: loadFormData.fee_type,
        fee_value: parseFloat(loadFormData.fee_value) || 0,
        unit_id: editingLoad.unit_id,
        week_start: editingLoad.week_start,
      };
      
      await loadsApi.update(editingLoad.id, updateData);
      toast.success('Load updated successfully');
      setEditLoadDialogOpen(false);
      setEditingLoad(null);
      loadSummaries(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update load');
    }
  };

  const loadSummaries = async () => {
    // In range mode, wait for both dates before fetching
    if (dateMode === 'range' && (!rangeStart || !rangeEnd)) return;

    setLoading(true);
    try {
      const metaPromise = dateMode === 'week'
        ? settlementApi.getMeta(weekStart).then((r) => r.data).catch(() => ({ statuses: {}, notes: {} }))
        : null;
      const res = dateMode === 'range'
        ? await summaryApi.getRange(rangeStart, rangeEnd)
        : await summaryApi.getAll(weekStart);
      setSummaries(res.data);

      // Settlement status / payroll notes are week-specific — only fetch in week mode
      if (dateMode === 'week') {
        const meta = await metaPromise;
        const newStatus = {};
        const newNotes = {};
        res.data.forEach((s) => {
          newStatus[s.unit_id] = meta.statuses?.[s.unit_id] || { is_paid: false, paid_at: null };
          newNotes[s.unit_id] = meta.notes?.[s.unit_id] || { current: { notes: '', acknowledged: false }, previous: null };
        });
        setSettlementStatus(newStatus);
        setPayrollNotes(newNotes);
      } else {
        setSettlementStatus({});
        setPayrollNotes({});
      }
    } catch (error) {
      toast.error('Failed to load summaries');
    } finally {
      setLoading(false);
    }
  };

  const handlePaidToggle = async (unitId, isPaid) => {
    try {
      await settlementApi.updateStatus({
        unit_id: unitId,
        week_start: weekStart,
        is_paid: isPaid
      });
      setSettlementStatus(prev => ({
        ...prev,
        [unitId]: { ...prev[unitId], is_paid: isPaid, paid_at: isPaid ? new Date().toISOString() : null }
      }));
      toast.success(`Settlement marked as ${isPaid ? 'paid' : 'unpaid'}`);
    } catch (error) {
      toast.error('Failed to update settlement status');
    }
  };

  const handleSaveNotes = async (unitId) => {
    try {
      await settlementApi.updatePayrollNotes({
        unit_id: unitId,
        week_start: weekStart,
        notes: notesText,
        acknowledged: false
      });
      setPayrollNotes(prev => ({
        ...prev,
        [unitId]: { ...prev[unitId], current: { notes: notesText, acknowledged: false } }
      }));
      setEditingNotes(null);
      toast.success('Payroll notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleAcknowledgePrevNotes = async (unitId) => {
    const prevWeekStart = addDaysYMD(weekStart, -7);
    
    try {
      await settlementApi.updatePayrollNotes({
        unit_id: unitId,
        week_start: prevWeekStart,
        notes: payrollNotes[unitId]?.previous?.notes || '',
        acknowledged: true
      });
      setPayrollNotes(prev => ({
        ...prev,
        [unitId]: { ...prev[unitId], previous: null }
      }));
      toast.success('Previous notes acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge notes');
    }
  };

  const navigateWeek = (direction) => {
    setWeekStart(addDaysYMD(weekStart, direction * 7));
  };

  const handleExportPdf = async (unitId, unitNumber) => {
    setExporting(unitId);
    try {
      const res = await summaryApi.exportPdf(unitId, weekStart, itemizedDeductions);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in PDF viewer
      setPdfUrl(url);
      setPdfUnitNumber(unitNumber);
      setPdfViewerOpen(true);
      
      return blob; // Return blob for sharing
    } catch (error) {
      toast.error('Failed to export PDF');
      return null;
    } finally {
      setExporting(null);
    }
  };
  
  // Download PDF from viewer
  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Settlement_Pay_${pdfUnitNumber}_${weekStart}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('PDF downloaded');
    }
  };
  
  // Close PDF viewer and cleanup
  const closePdfViewer = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfViewerOpen(false);
    setPdfUrl(null);
    setPdfUnitNumber('');
  };

  // Bulk: email every active unit's owner-operator the week's settlement PDF
  const handleEmailAllSettlements = async () => {
    if (!window.confirm(
      `Send the Week of ${getWeekRange(weekStart)} settlement PDF to the owner-operator of every active unit?`
    )) return;
    setBulkEmailing(true);
    try {
      const res = await settlementEmailsApi.emailAll(weekStart);
      const { sent = 0, skipped = 0, failed = 0 } = res.data || {};
      if (sent > 0 && failed === 0 && skipped === 0) {
        toast.success(`All ${sent} settlements emailed`);
      } else if (failed > 0) {
        toast.warning(`Sent ${sent} • Skipped ${skipped} • Failed ${failed}`);
      } else {
        toast.success(`Sent ${sent} • Skipped ${skipped}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk email failed');
    } finally {
      setBulkEmailing(false);
    }
  };

  // Server-side Email Settlement — seamless one-click send.
  // Automatically emails the rich settlement PDF to the unit's owner-operator on file.
  // No prompt, no extra clicks — just a toast.
  const handleEmailSettlement = async (summary, recipient = null) => {
    setSharing(summary.unit_id);
    try {
      const res = await summaryApi.emailSettlement(summary.unit_id, weekStart, recipient, '');
      toast.success(`Settlement emailed to ${res.data?.sent_to || 'owner-operator'}`);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (!recipient && detail.startsWith('NO_RECIPIENT')) {
        setSettlementEmailUnit(summary);
      } else {
        toast.error(detail || 'Failed to send settlement email');
        if (recipient) throw e;
      }
    } finally {
      setSharing(null);
    }
  };

  // Share via Email - Uses native share with PDF attachment on mobile, mailto fallback on desktop
  const handleShareEmail = async (summary) => {
    setSharing(summary.unit_id);
    try {
      // Get the PDF blob
      const res = await summaryApi.exportPdf(summary.unit_id, weekStart, itemizedDeductions);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const file = new File([blob], `Settlement_Pay_${summary.unit_number}_${weekStart}.pdf`, { type: 'application/pdf' });

      // Try native share with file attachment (works on mobile)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Settlement Pay - Unit ${summary.unit_number}`,
          text: `Hi ${summary.driver_name || 'Driver'},\n\nPlease find attached your settlement pay statement for the week of ${getWeekRange(weekStart)}.\n\nNet Payout: $${summary.net_payout.toLocaleString('en-US', { minimumFractionDigits: 2 })}\n\nPlease review and let me know if you have any questions.\n\nBest regards,\nAccounting`,
          files: [file]
        });
        toast.success('Shared successfully!');
      } else {
        // Fallback: Download PDF and open mailto
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Settlement_Pay_${summary.unit_number}_${weekStart}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Open mailto with proper formatting
        const driverEmail = summary.driver_email || '';
        const driverName = summary.driver_name || 'Driver';
        const weekRange = getWeekRange(weekStart);
        const netPayout = summary.net_payout.toLocaleString('en-US', { minimumFractionDigits: 2 });
        
        const subject = encodeURIComponent(`Settlement Pay - Unit ${summary.unit_number} - ${weekRange}`);
        const body = encodeURIComponent(
`Hi ${driverName},

Please find attached your settlement pay statement for the week of ${weekRange}.

Net Payout: $${netPayout}

Please review and let me know if you have any questions.

Best regards,
Accounting`
        );
        
        window.location.href = `mailto:${driverEmail}?subject=${subject}&body=${body}`;
        
        toast.success('PDF downloaded! Please attach it to the email.');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    } finally {
      setSharing(null);
    }
  };

  // Native share (for mobile/supported browsers)
  const handleNativeShare = async (summary) => {
    setSharing(summary.unit_id);
    try {
      // Get the PDF blob
      const res = await summaryApi.exportPdf(summary.unit_id, weekStart, itemizedDeductions);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const file = new File([blob], `Settlement_Pay_${summary.unit_number}_${weekStart}.pdf`, { type: 'application/pdf' });

      // Check if we can share files
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Settlement Pay - Unit ${summary.unit_number}`,
          files: [file]
        });
        toast.success('Shared successfully!');
      } else {
        // Fallback: just share file without text
        await navigator.share({
          title: `Settlement Pay - Unit ${summary.unit_number}`,
          files: [file]
        });
        toast.success('Shared successfully!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    } finally {
      setSharing(null);
    }
  };

  // Filter summaries
  const orderRank = useMemo(() => new Map(unitOrder.map((id, i) => [id, i])), [unitOrder]);
  const byOrder = (a, b) => (orderRank.get(a) ?? Infinity) - (orderRank.get(b) ?? Infinity);
  const filteredSummaries = (selectedUnit === 'all' 
    ? summaries 
    : summaries.filter(s => s.unit_id === selectedUnit)).slice().sort((a, b) => byOrder(a.unit_id, b.unit_id));

  const arrangeItems = useMemo(() => {
    const drv = new Map(summaries.map((s) => [s.unit_id, s.driver_name]));
    return units
      .filter((u) => u.status === 'active' || drv.has(u.id))
      .map((u) => ({ id: u.id, unit_number: u.unit_number, driver_name: drv.get(u.id) }))
      .sort((a, b) => byOrder(a.id, b.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, summaries, orderRank]);

  const handleSaveOrder = async (ids) => {
    setSavingOrder(true);
    try {
      const res = await preferencesApi.saveSummaryUnitOrder(ids);
      setUnitOrder(res.data?.unit_ids || ids);
      setArrangeOpen(false);
      toast.success('Unit order saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save order');
    } finally {
      setSavingOrder(false);
    }
  };

  const openUnitFromChart = (unitId) => {
    setExpandedUnits((prev) => ({ ...prev, [unitId]: true }));
    setTimeout(() => document.querySelector(`[data-testid="summary-unit-${unitId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  // Calculate totals
  // NOTE: the "Total Net Payout" aggregate only sums POSITIVE unit nets — a unit
  // in the red (net < 0) doesn't reduce what will be paid out to the other units.
  // That negative balance carries over to the next week for that specific unit; it
  // must not offset payouts owed to other owner-operators. Gross / fees / deductions
  // still sum normally so the breakdown stays accurate.
  const totals = filteredSummaries.reduce((acc, s) => ({
    gross: acc.gross + s.total_gross,
    fees: acc.fees + s.total_dispatch_fees,
    deductions: acc.deductions + s.total_deductions,
    net: acc.net + (s.net_payout || 0),
    loads: acc.loads + s.total_loads,
  }), { gross: 0, fees: 0, deductions: 0, net: 0, loads: 0 });

  // Bar chart data per unit
  const barData = filteredSummaries.map(s => ({
    id: s.unit_id,
    name: s.unit_number,
    driver: s.driver_name,
    gross: s.total_gross,
    net: s.net_payout,
    deductions: s.total_deductions + s.total_dispatch_fees,
  }));

  if (loading) {
    return (
      <div className="space-y-6" data-testid="summary-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="summary-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="dash-rise">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Settlements</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-tight text-slate-900 dark:text-white mt-1">Weekly Summary</h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-1.5">Financial overview and per-unit settlements</p>
        </div>
      </div>

      {/* Week Navigation & Filters */}
      <Card className="rounded-2xl">
        <CardContent className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <DateRangePicker
              mode={dateMode}
              onModeChange={setDateMode}
              weekStart={weekStart}
              onWeekStartChange={setWeekStart}
              startDate={rangeStart}
              endDate={rangeEnd}
              onRangeChange={(s, e) => { setRangeStart(s); setRangeEnd(e); }}
            />

            {/* Unit Filter - Hide for drivers */}
            {!isDriver && (
            <Select
              value={selectedUnit}
              onValueChange={setSelectedUnit}
            >
              <SelectTrigger className="w-[200px]" data-testid="unit-filter-select">
                <SelectValue placeholder="Filter by unit" />
              </SelectTrigger>
              <SelectContent>
                <UnitSelectItems units={units} includeAll allLabel="All Units" />
              </SelectContent>
            </Select>
            )}
            {!isDriver && (
              <Button variant="outline" onClick={() => setArrangeOpen(true)} data-testid="arrange-units-btn">
                <ArrowUpDown className="h-4 w-4 mr-1.5" /> Arrange units
              </Button>
            )}
            <ArrangeUnitsDialog open={arrangeOpen} onOpenChange={setArrangeOpen} units={arrangeItems} onSave={handleSaveOrder} saving={savingOrder} />

            {/* Bulk Email Settlements — non-driver, week-mode only, opt-in via Company Settings */}
            {!isDriver && dateMode === 'week' && bulkEmailEnabled && (
              <Button
                onClick={handleEmailAllSettlements}
                disabled={bulkEmailing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="email-all-settlements"
              >
                {bulkEmailing ? (
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Email All Settlements
                  </>
                )}
              </Button>
            )}
            
            {/* Driver badge showing assigned unit */}
            {isDriver && (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 rounded-lg">
                <Truck className="h-4 w-4" />
                <span className="font-medium">Unit: {user?.assigned_unit_number || 'Not Assigned'}</span>
              </div>
            )}
          </div>
          {dateMode === 'range' && rangeStart && rangeEnd && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="range-mode-note">
              Showing aggregated data for <strong>{rangeStart}</strong> to <strong>{rangeEnd}</strong>. Settlement Paid status, payroll notes, and PDF export are only available in <strong>Week</strong> view.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tab switch: unit settlements vs driver paystubs */}
      {!isDriver && (
        <div className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-1" data-testid="summary-tab-switch">
          {[['units', 'Unit Settlements'], ['drivers', 'Driver Paystubs']].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setSummaryTab(k)}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors ${summaryTab === k ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}
              data-testid={`summary-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {summaryTab === 'drivers' && !isDriver && (
        dateMode === 'week'
          ? <DriverPaystubsTab weekStart={weekStart} unitFilter={selectedUnit} />
          : <div className="rounded-2xl border p-6 text-base text-slate-600 dark:text-slate-300" data-testid="driver-paystubs-week-only">Driver paystubs are weekly — switch the date picker back to <strong>Week</strong>.</div>
      )}

      {(summaryTab === 'units' || isDriver) && (
      <>
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-4" data-testid="summary-stats">
        <StatTile label="Gross Revenue" value={formatCurrency(totals.gross)} accent="#1e3a8a" testid="summary-stat-gross" />
        <StatTile label="Dispatch Fees" value={`−${formatCurrency(totals.fees)}`} accent="#93c5fd" delay={60} testid="summary-stat-fees" />
        <StatTile label="Deductions" value={`−${formatCurrency(totals.deductions)}`} accent="#475569" delay={120} testid="summary-stat-deductions" />
        <StatTile label="Net Payout" value={formatCurrency(totals.net)} tone={totals.net < 0 ? "negative" : "primary"} delay={180} className="md:col-span-2 2xl:col-span-1" testid="summary-stat-net" />
        <StatTile label="Total Loads" value={totals.loads} accent="#3b82f6" delay={240} className="col-span-2 md:col-span-1" footer={`${filteredSummaries.length} unit${filteredSummaries.length === 1 ? '' : 's'}`} testid="summary-stat-loads" />
      </div>

      {/* Charts */}
      {filteredSummaries.length > 0 && (
        <>
          <RevenueSplitCard
            title="Revenue Breakdown"
            subtitle={dateMode === 'range' ? 'Where gross went · selected range' : 'Where gross went · this week'}
            testid="revenue-breakdown-card"
            delay={120}
            last30={{ gross: totals.gross, net: totals.net, fees: totals.fees, deductions: totals.deductions }}
            stats={[
              { label: 'Gross revenue', value: formatCurrency(totals.gross) },
              { label: 'Loads', value: totals.loads },
              { label: 'Avg gross / load', value: formatCurrency(totals.loads ? totals.gross / totals.loads : 0) },
            ]}
          />
          <DashCard title="Per Unit Comparison" subtitle="Gross vs net payout by unit" icon={BarChart3} delay={180} testid="per-unit-comparison-card"
            action={
              <div className="hidden sm:flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: DASH_COLORS.gross }} /> Gross</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: DASH_COLORS.fees }} /> Net</span>
              </div>
            }
          >
            <div className="md:hidden"><UnitBarsList data={barData} onSelect={openUnitFromChart} /></div>
            <div className="hidden md:block h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} barGap={4} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="4 6" stroke="currentColor" className="text-slate-200 dark:text-slate-800" />
                  <XAxis dataKey="name" tick={{ fontSize: 13, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={compactMoney} tick={{ fontSize: 13, fill: '#64748b' }} axisLine={false} tickLine={false} width={64} />
                  <Tooltip cursor={{ fill: 'rgba(59,130,246,0.08)' }} content={<DashTooltip labelFormatter={(v) => `Unit ${v}`} />} />
                  <Bar dataKey="gross" name="Gross" fill={DASH_COLORS.gross} radius={[6, 6, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="net" name="Net" fill={DASH_COLORS.fees} radius={[6, 6, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </DashCard>
        </>
      )}

      {/* Per Unit Summaries */}
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-manrope font-semibold text-slate-900 dark:text-white">Unit Summaries</h2>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-0.5">Click a unit to view its loads and deductions</p>
        </div>

        {filteredSummaries.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Truck className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No data for this week. Start by adding loads!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredSummaries.map((summary) => (
              <Collapsible
                key={summary.unit_id}
                open={expandedUnits[summary.unit_id]}
                onOpenChange={() => toggleUnitExpand(summary.unit_id)}
              >
                <Card className="rounded-2xl overflow-hidden scroll-mt-24" data-testid={`summary-unit-${summary.unit_id}`}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <div className="flex items-start justify-between gap-2 flex-wrap sm:flex-nowrap">
                        <CardTitle className="flex items-center gap-3 text-lg sm:text-xl min-w-0 shrink">
                          <span className="h-9 w-9 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0"><Truck className="h-5 w-5" /></span>
                          <span className="min-w-0 flex flex-col xl:flex-row xl:items-center xl:gap-3">
                            <span className="truncate text-slate-900 dark:text-white">Unit {summary.unit_number}</span>
                            {summary.driver_name && <span className="text-sm font-normal text-slate-600 dark:text-slate-300 truncate" data-testid={`summary-unit-driver-${summary.unit_id}`}>{summary.driver_name}</span>}
                          </span>
                          {expandedUnits[summary.unit_id] ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                          )}
                        </CardTitle>
                        <div className="grid grid-cols-3 gap-2 w-full sm:w-auto sm:flex sm:items-center sm:gap-1.5 sm:flex-wrap sm:justify-end" data-testid={`unit-actions-${summary.unit_id}`} onClick={(e) => e.stopPropagation()}>
                          {dateMode === 'week' && (
                          <>
                          {/* Email Settlement — standalone button (auto-sends to owner-op) */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEmailSettlement(summary)}
                            disabled={sharing === summary.unit_id}
                            className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-500/40 dark:text-blue-300 dark:hover:bg-blue-500/10 dark:hover:text-blue-200 dark:disabled:bg-transparent dark:disabled:text-blue-300 disabled:opacity-70 h-11 sm:h-8 w-full sm:w-auto px-2 sm:px-3 text-sm"
                            data-testid={`email-settlement-${summary.unit_id}`}
                          >
                            {sharing === summary.unit_id ? (
                              <span className="animate-spin h-4 w-4 border-2 border-blue-600 dark:border-blue-300 border-t-transparent rounded-full" />
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-1.5 sm:mr-1" />
                                <span className="sm:hidden">Email</span>
                                <span className="hidden sm:inline">Email Settlement</span>
                              </>
                            )}
                          </Button>

                          {/* Share Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={sharing === summary.unit_id}
                                className="h-11 sm:h-8 w-full sm:w-auto px-2 sm:px-3 text-sm"
                                data-testid={`share-btn-${summary.unit_id}`}
                              >
                                {sharing === summary.unit_id ? (
                                  <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                                ) : (
                                  <>
                                    <Share2 className="h-4 w-4 mr-1.5 sm:mr-1" />
                                    <span>Share</span>
                                  </>
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => handleShareEmail(summary)}
                                data-testid={`share-email-${summary.unit_id}`}
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                Share via Email
                              </DropdownMenuItem>
                              {canNativeShare && (
                                <DropdownMenuItem 
                                  onClick={() => handleNativeShare(summary)}
                                  data-testid={`share-native-${summary.unit_id}`}
                                >
                                  <MessageCircle className="h-4 w-4 mr-2" />
                                  Share via Text/Apps
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          {/* View PDF Button (opens preview) */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExportPdf(summary.unit_id, summary.unit_number)}
                            disabled={exporting === summary.unit_id}
                            className="h-11 sm:h-8 w-full sm:w-auto px-2 sm:px-3 text-sm"
                            data-testid={`export-pdf-${summary.unit_id}`}
                          >
                            {exporting === summary.unit_id ? (
                              <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                            ) : (
                              <>
                                <Eye className="h-4 w-4 mr-1.5 sm:mr-1" />
                                <span>View</span>
                              </>
                            )}
                          </Button>
                          
                          {/* Paid Checkbox */}
                          {canEdit && !isDriver && (
                            <div 
                              className={`col-span-3 flex items-center justify-center sm:justify-start gap-2 px-3 py-2.5 sm:py-1.5 rounded-lg border ${
                                settlementStatus[summary.unit_id]?.is_paid 
                                  ? 'bg-blue-50 border-blue-300 dark:bg-blue-950/40 dark:border-blue-700' 
                                  : 'bg-slate-50 border-slate-200 dark:bg-slate-800'
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox 
                                id={`paid-${summary.unit_id}`}
                                checked={settlementStatus[summary.unit_id]?.is_paid || false}
                                onCheckedChange={(checked) => handlePaidToggle(summary.unit_id, checked)}
                                className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                              />
                              <label 
                                htmlFor={`paid-${summary.unit_id}`} 
                                className={`text-sm font-medium cursor-pointer ${
                                  settlementStatus[summary.unit_id]?.is_paid 
                                    ? 'text-blue-700 dark:text-blue-300' 
                                    : 'text-muted-foreground'
                                }`}
                              >
                                {settlementStatus[summary.unit_id]?.is_paid ? 'Paid ✓' : 'Paid?'}
                              </label>
                            </div>
                          )}
                          </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CardContent>
                    {/* Previous Week Notes Carryover Alert */}
                    {dateMode === 'week' && payrollNotes[summary.unit_id]?.previous && !isDriver && (
                      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-blue-900 dark:text-blue-200 text-sm">Previous Week Note (Unacknowledged)</p>
                            <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">{payrollNotes[summary.unit_id].previous.notes}</p>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100 dark:text-blue-300 dark:border-blue-700"
                              onClick={(e) => { e.stopPropagation(); handleAcknowledgePrevNotes(summary.unit_id); }}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Mark as Acknowledged
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Payroll Notes Section */}
                    {dateMode === 'week' && canEdit && !isDriver && (
                      <div className="mb-4">
                        {editingNotes === summary.unit_id ? (
                          <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-2">
                              <StickyNote className="h-4 w-4 text-blue-600" />
                              <span className="text-sm font-medium">Payroll Notes</span>
                            </div>
                            <Textarea
                              value={notesText}
                              onChange={(e) => setNotesText(e.target.value)}
                              placeholder="Add payroll notes for this week..."
                              rows={3}
                              className="text-sm bg-white dark:bg-slate-900"
                            />
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => handleSaveNotes(summary.unit_id)}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                Save Notes
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => setEditingNotes(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setEditingNotes(summary.unit_id); 
                              setNotesText(payrollNotes[summary.unit_id]?.current?.notes || '');
                            }}
                          >
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <StickyNote className="h-4 w-4" />
                              {payrollNotes[summary.unit_id]?.current?.notes ? (
                                <span className="text-foreground">{payrollNotes[summary.unit_id].current.notes}</span>
                              ) : (
                                <span className="italic">Click to add payroll notes...</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Summary Stats — payroll receipt style */}
                    <div className="mb-4 rounded-xl border border-blue-200/80 dark:border-blue-900/60 overflow-hidden shadow-sm" data-testid={`unit-summary-stats-${summary.unit_id}`}>
                      <div className={`grid grid-cols-2 sm:grid-cols-3 ${summary.total_additions ? 'xl:grid-cols-6' : 'xl:grid-cols-5'} gap-px bg-blue-200/70 dark:bg-blue-900/50 [&>div]:bg-gradient-to-br [&>div]:from-blue-100/80 [&>div]:to-blue-50/60 dark:[&>div]:from-slate-800 dark:[&>div]:to-slate-900`}>
                        <div className="p-4 flex flex-col items-start justify-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Loads</span>
                          <span className="text-2xl font-semibold text-slate-900 dark:text-white font-mono mt-1">{summary.total_loads}</span>
                        </div>
                        <div className="p-4 flex flex-col items-start justify-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Gross</span>
                          <span className="text-lg sm:text-xl 2xl:text-2xl font-semibold font-mono whitespace-nowrap text-slate-900 dark:text-white mt-1 whitespace-nowrap">{formatCurrency(summary.total_gross)}</span>
                        </div>
                        {summary.total_additions ? (
                          <div className="p-4 flex flex-col items-start justify-center">
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">+ Additions</span>
                            <span className="text-lg sm:text-xl 2xl:text-2xl font-semibold font-mono whitespace-nowrap text-blue-700 dark:text-blue-300 mt-1">+{formatCurrency(summary.total_additions)}</span>
                          </div>
                        ) : null}
                        <div className="p-4 flex flex-col items-start justify-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">− Dispatch Fees</span>
                          <span className="text-lg sm:text-xl 2xl:text-2xl font-semibold font-mono whitespace-nowrap text-slate-600 dark:text-slate-300 mt-1">−{formatCurrency(summary.total_dispatch_fees)}</span>
                        </div>
                        <div className="p-4 flex flex-col items-start justify-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">− Deductions</span>
                          <span className="text-lg sm:text-xl 2xl:text-2xl font-semibold font-mono whitespace-nowrap text-slate-600 dark:text-slate-300 mt-1">−{formatCurrency(summary.total_deductions)}</span>
                        </div>
                        <div className={`p-4 flex flex-col items-start justify-center ${summary.total_additions ? '' : 'col-span-2 sm:col-span-2 xl:col-span-1'} ${summary.net_payout < 0 ? '!bg-none !bg-rose-50 dark:!bg-rose-950/40' : 'surface-primary !border-0'}`} data-testid={`unit-net-${summary.unit_id}`}>
                          <span className={`text-sm font-semibold ${summary.net_payout < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-blue-900/80 dark:text-blue-100'}`}>= Net Payout</span>
                          <span className={`text-xl 2xl:text-2xl font-bold font-mono mt-1 whitespace-nowrap ${summary.net_payout < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-[#0f2a5c] dark:text-white'}`}>
                            {summary.net_payout < 0 ? `−${formatCurrency(Math.abs(summary.net_payout))}` : formatCurrency(summary.net_payout)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Expandable Details */}
                    <CollapsibleContent className="space-y-4">
                      {/* Loads Table */}
                      <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                        <div className="bg-slate-50 dark:bg-slate-800 px-4 py-3 border-b dark:border-slate-700">
                          <h4 className="font-semibold flex items-center gap-2 text-foreground">
                            <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            Loads ({summary.loads?.length || 0})
                          </h4>
                        </div>
                        {summary.loads && summary.loads.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Reference</TableHead>
                                <TableHead>Broker</TableHead>
                                <TableHead>Route</TableHead>
                                <TableHead>Dates</TableHead>
                                <TableHead className="text-right">Payout</TableHead>
                                <TableHead className="text-right">Fee</TableHead>
                                <TableHead className="text-right">Net</TableHead>
                                {canEdit && !isDriver && <TableHead className="text-right">Actions</TableHead>}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {summary.loads.map((load) => (
                                <TableRow key={load.id} data-testid={`summary-load-row-${load.id}`}>
                                  <TableCell className="font-medium">
                                    <Badge variant="outline">{load.load_reference}</Badge>
                                  </TableCell>
                                  <TableCell>{load.broker_name}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1 text-sm">
                                      <MapPin className="h-3 w-3 text-muted-foreground" />
                                      <span className="truncate max-w-[80px]">{load.pickup_location}</span>
                                      <span className="text-muted-foreground">→</span>
                                      <span className="truncate max-w-[80px]">{load.delivery_location}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {formatDate(load.start_date)} - {formatDate(load.end_date)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono">
                                    {formatCurrency(load.payout_amount)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-slate-600 dark:text-slate-300">
                                    -{formatCurrency(load.dispatch_fee)}
                                    <span className="text-xs ml-1">
                                      ({load.fee_type === 'percentage' ? `${load.fee_value}%` : 'Flat'})
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right font-mono font-semibold text-blue-700 dark:text-blue-300">
                                    {formatCurrency(load.net_payout)}
                                  </TableCell>
                                  {canEdit && !isDriver && (
                                    <TableCell className="text-right">
                                      <div className="flex justify-end gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={(e) => { e.stopPropagation(); handleEditLoad(load); }}
                                          data-testid={`summary-edit-load-${load.id}`}
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={(e) => { e.stopPropagation(); handleDeleteLoad(load.id); }}
                                          className="text-red-600 hover:text-red-700"
                                          data-testid={`summary-delete-load-${load.id}`}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="text-center py-6 text-muted-foreground text-sm">
                            <Package className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                            No loads recorded for this week
                          </div>
                        )}
                      </div>

                      {/* Deductions Table */}
                      <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                        <div className="bg-slate-50 dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                          <h4 className="font-semibold flex items-center gap-2 text-slate-900 dark:text-white">
                            <Fuel className="h-4 w-4" />
                            Deductions ({summary.deductions?.length || 0})
                          </h4>
                          {canEdit && !isDriver && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40"
                              onClick={(e) => { e.stopPropagation(); handleAddDeduction(summary.unit_id, weekStart); }}
                              data-testid={`summary-add-deduction-${summary.unit_id}`}
                            >
                              <Plus className="h-3 w-3 mr-1" />Add Deduction
                            </Button>
                          )}
                        </div>
                        {summary.deductions && summary.deductions.length > 0 ? (
                          <>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Notes</TableHead>
                                  <TableHead className="text-right">Amount</TableHead>
                                  {canEdit && !isDriver && <TableHead className="text-right">Actions</TableHead>}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {summary.deductions.map((deduction) => (
                                  <TableRow key={deduction.id} data-testid={`summary-deduction-row-${deduction.id}`}>
                                    <TableCell className="font-medium">
                                      {deduction.name === 'Previous Balance' || deduction.deduction_type === 'carry_over' ? (
                                        <button
                                          className="text-left hover:underline text-blue-700 dark:text-blue-300 flex items-center gap-1 cursor-pointer"
                                          onClick={(e) => { e.stopPropagation(); handleViewPreviousBalance(summary.unit_id, weekStart); }}
                                        >
                                          <AlertCircle className="h-3 w-3" />
                                          {deduction.name}
                                          <span className="text-xs text-muted-foreground">(click for details)</span>
                                        </button>
                                      ) : (
                                        deduction.name
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {deduction.deduction_type === 'carry_over' ? (
                                        <Badge variant="outline" className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300">
                                          Carry Over
                                        </Badge>
                                      ) : deduction.deduction_type && deduction.deduction_type !== 'standard' ? (
                                        <Badge variant="outline" className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                                          {deduction.deduction_type}
                                        </Badge>
                                      ) : (
                                        <span className="text-muted-foreground text-sm">Standard</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                                      {deduction.notes || '-'}
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-slate-700 dark:text-slate-200">
                                      -{formatCurrency(deduction.amount)}
                                    </TableCell>
                                    {canEdit && !isDriver && (
                                      <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => { e.stopPropagation(); handleEditDeduction(deduction); }}
                                            data-testid={`summary-edit-deduction-${deduction.id}`}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => { e.stopPropagation(); handleDeleteDeduction(deduction.id); }}
                                            className="text-red-600 hover:text-red-700"
                                            data-testid={`summary-delete-deduction-${deduction.id}`}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex justify-between items-center">
                              <span className="font-semibold text-slate-900 dark:text-white">Total Deductions</span>
                              <span className="font-mono font-bold text-slate-900 dark:text-white">
                                -{formatCurrency(summary.total_deductions)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-6 text-muted-foreground text-sm">
                            <DollarSign className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                            <p className="mb-3">No deductions recorded for this week</p>
                            {canEdit && !isDriver && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40"
                                onClick={(e) => { e.stopPropagation(); handleAddDeduction(summary.unit_id, weekStart); }}
                                data-testid={`summary-add-deduction-empty-${summary.unit_id}`}
                              >
                                <Plus className="h-3.5 w-3.5 mr-1.5" />Add First Deduction
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Additions Section (bonuses / refunds / adjustments — boosts net) */}
                      {summary.additions && summary.additions.length > 0 && (
                        <div className="border border-blue-200 dark:border-blue-800 rounded-xl overflow-hidden" data-testid={`summary-additions-${summary.unit_id}`}>
                          <div className="bg-blue-50 dark:bg-blue-950/40 px-4 py-3 border-b border-blue-200 dark:border-blue-800 flex items-center justify-between">
                            <h4 className="font-semibold flex items-center gap-2 text-blue-800 dark:text-blue-300">
                              <Plus className="h-4 w-4" />
                              Additions ({summary.additions.length})
                            </h4>
                            <span className="text-xs text-blue-700 dark:text-blue-300/80">Bonuses / refunds / adjustments</span>
                          </div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Notes</TableHead>
                                <TableHead>Linked Load</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {summary.additions.map((a) => {
                                const typeLabel = a.addition_type === 'custom' && a.custom_type_label
                                  ? a.custom_type_label
                                  : (a.addition_type || 'adjustment').charAt(0).toUpperCase() + (a.addition_type || 'adjustment').slice(1);
                                return (
                                  <TableRow key={a.id} data-testid={`summary-addition-row-${a.id}`}>
                                    <TableCell className="text-sm whitespace-nowrap">{a.date}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-300">
                                        {typeLabel}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{a.notes || '-'}</TableCell>
                                    <TableCell className="text-sm font-mono text-muted-foreground">{a.linked_load_reference || '-'}</TableCell>
                                    <TableCell className="text-right font-mono font-semibold text-blue-700 dark:text-blue-300 font-semibold">
                                      +{formatCurrency(a.amount)}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                          <div className="px-4 py-3 border-t border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 flex justify-between items-center">
                            <span className="font-semibold text-blue-800 dark:text-blue-300">Total Additions</span>
                            <span className="font-mono font-bold text-blue-800 dark:text-blue-300">
                              +{formatCurrency(summary.total_additions || 0)}
                            </span>
                          </div>
                        </div>
                      )}
                    </CollapsibleContent>
                  </CardContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        )}
      </div>
      </>
      )}

      <EmailPromptDialog
        open={!!settlementEmailUnit}
        onOpenChange={(o) => !o && setSettlementEmailUnit(null)}
        title={`Email Unit ${settlementEmailUnit?.unit_number || ''} settlement`}
        description="No owner-operator or driver email is on file for this unit. Enter who should receive it."
        onSubmit={(email) => handleEmailSettlement(settlementEmailUnit, email)}
      />

      {/* PDF Viewer Dialog */}
      <Dialog open={pdfViewerOpen} onOpenChange={closePdfViewer}>
        <DialogContent className="max-w-4xl h-[90vh] p-0">
          <DialogHeader className="p-4 pb-2 border-b flex flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Settlement Pay - Unit {pdfUnitNumber}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadPdf}
                data-testid="pdf-download-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={closePdfViewer}
                data-testid="pdf-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 h-full p-4 pt-0">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-[calc(90vh-80px)] border rounded-lg"
                title="Settlement Pay PDF"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Load Dialog */}
      <Dialog open={editLoadDialogOpen} onOpenChange={setEditLoadDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">Edit Load</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Load Reference</Label>
                <Input
                  value={loadFormData.load_reference}
                  onChange={(e) => setLoadFormData({...loadFormData, load_reference: e.target.value})}
                  placeholder="e.g., LD-001"
                />
              </div>
              <div className="space-y-2">
                <Label>Broker</Label>
                <Input
                  value={loadFormData.broker_name}
                  onChange={(e) => setLoadFormData({...loadFormData, broker_name: e.target.value})}
                  placeholder="Broker name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pickup Location</Label>
                <Input
                  value={loadFormData.pickup_location}
                  onChange={(e) => setLoadFormData({...loadFormData, pickup_location: e.target.value})}
                  placeholder="City, State"
                />
              </div>
              <div className="space-y-2">
                <Label>Delivery Location</Label>
                <Input
                  value={loadFormData.delivery_location}
                  onChange={(e) => setLoadFormData({...loadFormData, delivery_location: e.target.value})}
                  placeholder="City, State"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={loadFormData.start_date}
                  onChange={(e) => setLoadFormData({...loadFormData, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={loadFormData.end_date}
                  onChange={(e) => setLoadFormData({...loadFormData, end_date: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Payout Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={loadFormData.payout_amount}
                  onChange={(e) => setLoadFormData({...loadFormData, payout_amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Fee Type</Label>
                <Select
                  value={loadFormData.fee_type}
                  onValueChange={(v) => setLoadFormData({...loadFormData, fee_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat Rate ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{loadFormData.fee_type === 'percentage' ? 'Fee %' : 'Fee Amount ($)'}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={loadFormData.fee_value}
                  onChange={(e) => setLoadFormData({...loadFormData, fee_value: e.target.value})}
                  placeholder={loadFormData.fee_type === 'percentage' ? '10' : '100'}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLoadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveLoad} className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Deduction Dialog */}
      <Dialog open={editDeductionDialogOpen} onOpenChange={setEditDeductionDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {editingDeduction?.id ? 'Edit Deduction' : 'Add Deduction'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deduction Name</Label>
                <Input
                  value={deductionFormData.name}
                  onChange={(e) => setDeductionFormData({...deductionFormData, name: e.target.value})}
                  placeholder="e.g., Fuel, Insurance"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={deductionFormData.amount}
                  onChange={(e) => setDeductionFormData({...deductionFormData, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={deductionFormData.deduction_type}
                onValueChange={(v) => setDeductionFormData({...deductionFormData, deduction_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={deductionFormData.notes}
                onChange={(e) => setDeductionFormData({...deductionFormData, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDeductionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveDeduction} className="bg-blue-600 hover:bg-blue-700" data-testid="save-deduction-btn">
              {editingDeduction?.id ? 'Save Changes' : 'Add Deduction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Previous Balance Breakdown Dialog */}
      <Dialog open={prevBalanceDialogOpen} onOpenChange={setPrevBalanceDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              Previous Balance Breakdown
            </DialogTitle>
            {prevBalanceData && (
              <p className="text-sm text-muted-foreground mt-1">
                Showing data from week of <strong>{prevBalanceData.from_week}</strong> that resulted in carry-over to <strong>{prevBalanceData.to_week}</strong>
              </p>
            )}
          </DialogHeader>
          
          {loadingPrevBalance ? (
            <div className="py-8 text-center">
              <Skeleton className="h-32 w-full" />
            </div>
          ) : prevBalanceData ? (
            <div className="space-y-4">
              {/* Unit Info */}
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
                <p className="font-semibold">Unit: {prevBalanceData.unit_number}</p>
                <p className="text-sm text-muted-foreground">From week: {prevBalanceData.from_week}</p>
              </div>
              
              {/* Previous Week Summary */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 dark:bg-slate-800 px-4 py-3 border-b">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Previous Week Financial Summary</h4>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Total Gross Revenue</span>
                    <span className="font-mono">{formatCurrency(prevBalanceData.summary.total_gross)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600 dark:text-slate-300">
                    <span>Dispatch Fees</span>
                    <span className="font-mono">-{formatCurrency(prevBalanceData.summary.total_fees)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600 dark:text-slate-300">
                    <span>Deductions</span>
                    <span className="font-mono">-{formatCurrency(prevBalanceData.summary.total_deductions)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span className={prevBalanceData.summary.net_payout >= 0 ? 'text-blue-700 dark:text-blue-300' : 'text-rose-600'}>
                      Net Payout
                    </span>
                    <span className={`font-mono ${prevBalanceData.summary.net_payout >= 0 ? 'text-blue-700 dark:text-blue-300' : 'text-rose-600'}`}>
                      {formatCurrency(prevBalanceData.summary.net_payout)}
                    </span>
                  </div>
                  {prevBalanceData.summary.carried_amount > 0 && (
                    <div className="flex justify-between font-bold text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-950/40 p-2 rounded mt-2">
                      <span>Amount Carried Forward</span>
                      <span className="font-mono">{formatCurrency(prevBalanceData.summary.carried_amount)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Loads from Previous Week */}
              {prevBalanceData.loads && prevBalanceData.loads.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-blue-50 dark:bg-blue-900/30 px-4 py-3 border-b">
                    <h4 className="font-semibold text-blue-800 dark:text-blue-300">Loads ({prevBalanceData.loads.length})</h4>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reference</TableHead>
                        <TableHead>Broker</TableHead>
                        <TableHead className="text-right">Payout</TableHead>
                        <TableHead className="text-right">Fee</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {prevBalanceData.loads.map((load, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{load.load_reference}</TableCell>
                          <TableCell>{load.broker_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(load.payout_amount)}</TableCell>
                          <TableCell className="text-right font-mono text-slate-600 dark:text-slate-300">-{formatCurrency(load.dispatch_fee)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Deductions from Previous Week */}
              {prevBalanceData.deductions && prevBalanceData.deductions.length > 0 && (
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                    <h4 className="font-semibold text-slate-900 dark:text-white">Deductions ({prevBalanceData.deductions.length})</h4>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {prevBalanceData.deductions.map((ded, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{ded.name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{ded.notes || '-'}</TableCell>
                          <TableCell className="text-right font-mono text-slate-700 dark:text-slate-200">-{formatCurrency(ded.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No previous balance data available
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrevBalanceDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
